/**
 * Tabla única de compatibilidad entre clases SGA/IMDG de StorSafe.
 * Reemplaza a los 6 motores que existían por separado (Matriz de
 * Compatibilidad, Bodegas ×2, Reportes, Emergencias y Dashboard).
 *
 * Regla provisional (13/10/2026): veredicto más estricto entre la tabla IMDG
 * de la Matriz y los pares de Bodegas, mientras Kevin revisa. Caso a revisar:
 * Clase 9 queda en precaución con todas las clases (origen: Matriz).
 *
 * Cada par guarda su veredicto unificado, de qué tabla salió ('ambas',
 * 'matriz' o 'bodegas') y los dos veredictos anteriores, para esa revisión.
 * No se cita ninguna norma como fuente del veredicto.
 *
 * Tabla generada por script desde las dos tablas anteriores (diseño en
 * docs/diseno/compatibilidad_unica_2026-10.md). No editar a mano un par sin
 * actualizar compatibilidad.test.ts.
 */

export type NivelCompat = 'compatible' | 'precaucion' | 'incompatible';
export type OrigenVeredicto = 'ambas' | 'matriz' | 'bodegas';

export interface VeredictoPar {
  nivel: NivelCompat;
  origen: OrigenVeredicto;
  /** Veredicto que daba la Matriz de Compatibilidad antes de unificar. */
  matriz: NivelCompat;
  /** Veredicto que daban Bodegas/Reportes/Emergencias/Dashboard ('segregar' → 'precaucion'). */
  bodegas: NivelCompat;
}

export const CLASES_COMPAT = [
  'clase1', 'clase2-1', 'clase2-2', 'clase2-3', 'clase3', 'clase4-1', 'clase4-2',
  'clase4-3', 'clase5-1', 'clase5-2', 'clase6', 'clase7', 'clase8', 'clase9',
] as const;

const v = (nivel: NivelCompat, origen: OrigenVeredicto, matriz: NivelCompat, bodegas: NivelCompat): VeredictoPar =>
  ({ nivel, origen, matriz, bodegas });

/** 105 pares de clases (incluida la misma clase), clave en orden alfabético 'claseA|claseB'. */
export const TABLA_COMPATIBILIDAD: Readonly<Record<string, VeredictoPar>> = {
  'clase1|clase1':       v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase1|clase2-1':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase1|clase2-2':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase1|clase2-3':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase1|clase3':       v('incompatible', 'bodegas',      'precaucion',   'incompatible'),
  'clase1|clase4-1':     v('incompatible', 'bodegas',      'precaucion',   'incompatible'),
  'clase1|clase4-2':     v('incompatible', 'bodegas',      'precaucion',   'incompatible'),
  'clase1|clase4-3':     v('incompatible', 'bodegas',      'precaucion',   'incompatible'),
  'clase1|clase5-1':     v('incompatible', 'bodegas',      'precaucion',   'incompatible'),
  'clase1|clase5-2':     v('incompatible', 'bodegas',      'precaucion',   'incompatible'),
  'clase1|clase6':       v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase1|clase7':       v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase1|clase8':       v('incompatible', 'bodegas',      'precaucion',   'incompatible'),
  'clase1|clase9':       v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase2-1|clase2-1':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-1|clase2-2':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-1|clase2-3':   v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase2-1|clase3':     v('precaucion',   'bodegas',      'compatible',   'precaucion'),
  'clase2-1|clase4-1':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-1|clase4-2':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-1|clase4-3':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-1|clase5-1':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-1|clase5-2':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-1|clase6':     v('incompatible', 'matriz',       'incompatible', 'compatible'),
  'clase2-1|clase7':     v('incompatible', 'matriz',       'incompatible', 'compatible'),
  'clase2-1|clase8':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-1|clase9':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase2-2|clase2-2':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-2|clase2-3':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-2|clase3':     v('precaucion',   'bodegas',      'compatible',   'precaucion'),
  'clase2-2|clase4-1':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-2|clase4-2':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-2|clase4-3':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-2|clase5-1':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-2|clase5-2':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-2|clase6':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-2|clase7':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-2|clase8':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-2|clase9':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase2-3|clase2-3':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-3|clase3':     v('incompatible', 'matriz',       'incompatible', 'precaucion'),
  'clase2-3|clase4-1':   v('incompatible', 'matriz',       'incompatible', 'compatible'),
  'clase2-3|clase4-2':   v('incompatible', 'matriz',       'incompatible', 'compatible'),
  'clase2-3|clase4-3':   v('incompatible', 'matriz',       'incompatible', 'compatible'),
  'clase2-3|clase5-1':   v('incompatible', 'matriz',       'incompatible', 'compatible'),
  'clase2-3|clase5-2':   v('incompatible', 'matriz',       'incompatible', 'compatible'),
  'clase2-3|clase6':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-3|clase7':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase2-3|clase8':     v('incompatible', 'matriz',       'incompatible', 'compatible'),
  'clase2-3|clase9':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase3|clase3':       v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase3|clase4-1':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase3|clase4-2':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase3|clase4-3':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase3|clase5-1':     v('incompatible', 'ambas',        'incompatible', 'incompatible'),
  'clase3|clase5-2':     v('incompatible', 'bodegas',      'compatible',   'incompatible'),
  'clase3|clase6':       v('precaucion',   'ambas',        'precaucion',   'precaucion'),
  'clase3|clase7':       v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase3|clase8':       v('incompatible', 'bodegas',      'compatible',   'incompatible'),
  'clase3|clase9':       v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase4-1|clase4-1':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase4-1|clase4-2':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase4-1|clase4-3':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase4-1|clase5-1':   v('incompatible', 'ambas',        'incompatible', 'incompatible'),
  'clase4-1|clase5-2':   v('incompatible', 'bodegas',      'compatible',   'incompatible'),
  'clase4-1|clase6':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase4-1|clase7':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase4-1|clase8':     v('incompatible', 'matriz',       'incompatible', 'compatible'),
  'clase4-1|clase9':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase4-2|clase4-2':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase4-2|clase4-3':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase4-2|clase5-1':   v('incompatible', 'ambas',        'incompatible', 'incompatible'),
  'clase4-2|clase5-2':   v('incompatible', 'bodegas',      'compatible',   'incompatible'),
  'clase4-2|clase6':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase4-2|clase7':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase4-2|clase8':     v('incompatible', 'matriz',       'incompatible', 'compatible'),
  'clase4-2|clase9':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase4-3|clase4-3':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase4-3|clase5-1':   v('incompatible', 'ambas',        'incompatible', 'incompatible'),
  'clase4-3|clase5-2':   v('incompatible', 'bodegas',      'compatible',   'incompatible'),
  'clase4-3|clase6':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase4-3|clase7':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase4-3|clase8':     v('incompatible', 'matriz',       'incompatible', 'compatible'),
  'clase4-3|clase9':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase5-1|clase5-1':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase5-1|clase5-2':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase5-1|clase6':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase5-1|clase7':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase5-1|clase8':     v('incompatible', 'ambas',        'incompatible', 'incompatible'),
  'clase5-1|clase9':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase5-2|clase5-2':   v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase5-2|clase6':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase5-2|clase7':     v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase5-2|clase8':     v('incompatible', 'ambas',        'incompatible', 'incompatible'),
  'clase5-2|clase9':     v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase6|clase6':       v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase6|clase7':       v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase6|clase8':       v('precaucion',   'ambas',        'precaucion',   'precaucion'),
  'clase6|clase9':       v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase7|clase7':       v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase7|clase8':       v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase7|clase9':       v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase8|clase8':       v('compatible',   'ambas',        'compatible',   'compatible'),
  'clase8|clase9':       v('precaucion',   'matriz',       'precaucion',   'compatible'),
  'clase9|clase9':       v('compatible',   'ambas',        'compatible',   'compatible'),
};

const COMPATIBLE: VeredictoPar = v('compatible', 'ambas', 'compatible', 'compatible');
const RANGO: Record<NivelCompat, number> = { compatible: 0, precaucion: 1, incompatible: 2 };

/** 'clase2' (id antiguo, sin división) se lee como 'clase2-1', igual que hacía la Matriz. */
export function normalizarClase(clase: string): string {
  return clase === 'clase2' ? 'clase2-1' : clase;
}

export function claveDePar(a: string, b: string): string {
  return [normalizarClase(a), normalizarClase(b)].sort().join('|');
}

/** 'no_clasificado' o una clase desconocida → compatible (como antes en los 6 motores). */
export function veredictoClases(a: string, b: string): VeredictoPar {
  if (a === 'no_clasificado' || b === 'no_clasificado') return COMPATIBLE;
  return TABLA_COMPATIBILIDAD[claveDePar(a, b)] ?? COMPATIBLE;
}

export function nivelClases(a: string, b: string): NivelCompat {
  return veredictoClases(a, b).nivel;
}

export function peorNivel(a: NivelCompat, b: NivelCompat): NivelCompat {
  return RANGO[a] >= RANGO[b] ? a : b;
}

interface ConClases { sga_classes?: string[] | null }

/** Peor nivel entre todas las combinaciones de clases de dos productos. Sin clases → compatible. */
export function nivelProductos(a: ConClases, b: ConClases): NivelCompat {
  let peor: NivelCompat = 'compatible';
  for (const ca of a.sga_classes ?? []) {
    for (const cb of b.sga_classes ?? []) {
      peor = peorNivel(peor, nivelClases(ca, cb));
      if (peor === 'incompatible') return peor;
    }
  }
  return peor;
}

/** Pares no compatibles dentro de un grupo (zona, bodega…). Nunca compara un producto consigo mismo. */
export function paresConConflicto<T extends ConClases>(
  productos: T[],
): { a: T; b: T; nivel: Exclude<NivelCompat, 'compatible'> }[] {
  const pares: { a: T; b: T; nivel: Exclude<NivelCompat, 'compatible'> }[] = [];
  for (let i = 0; i < productos.length; i++) {
    for (let j = i + 1; j < productos.length; j++) {
      const nivel = nivelProductos(productos[i], productos[j]);
      if (nivel !== 'compatible') pares.push({ a: productos[i], b: productos[j], nivel });
    }
  }
  return pares;
}

/** Peor nivel de un grupo de productos. */
export function nivelGrupo(productos: ConClases[]): NivelCompat {
  let peor: NivelCompat = 'compatible';
  for (const p of paresConConflicto(productos)) {
    peor = peorNivel(peor, p.nivel);
    if (peor === 'incompatible') break;
  }
  return peor;
}
